import { ButtonHTMLAttributes, FunctionComponent } from 'preact';
import { Config, useConfig } from '../providers/ConfigProvider';
import { useUnifiedWidget } from '../providers/UnifiedWidgetProvider';
import CloseIcon from '../icons/Close';
import OpenIcon from '../icons/Open';

type WidgetButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  config: Config;
  open: boolean;
}

const WidgetButtonStyled = ({ config, open, children, ref, ...props }: WidgetButtonProps) => {
  const { gooberCss } = useUnifiedWidget();

  return (
    <button
      ref={ref}
      {...props}
      className={gooberCss({
        display: "flex",
        flexShrink: 0,
        position: "relative",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        marginTop: "10px",
        cursor: "pointer",
        border: "none",
        height: `${config.buttonSize}px`,
        minWidth: `${config.buttonSize}px`,
        padding: `${config.buttonSize / 5}px`,
        borderRadius: `${config.buttonSize / 2}px`,
        color: config.buttonTextColor,
        fontSize: "18px",
        backgroundColor: config.buttonBgColor,
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
        transition: "transform 0.3s ease, box-shadow 0.3s ease, background-color 0.3s ease",
        "&:hover": {
          transform: "scale(1.08)",
          backgroundColor: config.buttonBgColor,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
        },
        "&:active": {
          transform: "scale(0.92)",
          transition: "all 50ms ease",
          backgroundColor: config.buttonBgColor,
        },
        "&:focus-visible": {
          outline: "2px solid currentColor",
        },
        "&:focus:not(:focus-visible)": {
          outline: "none",
        },
        "& .icon": {
          position: "relative",
          height: `${config.buttonSize * 0.6}px`,
          width: `${config.buttonSize * 0.6}px`,
        },
        "& svg": {
          top: 0,
          left: 0,
          position: "absolute",
          width: "100%",
          height: "100%",
          transition: "all 0.2s ease-in-out",
        },
        ...( open ? {
          "svg.openSvg": {
            transform: "rotate(20deg) scale(0.5)",
            visibility: "hidden",
            opacity: 0,
          },
          "svg.closeSvg": {
            transition: "all .1s ease-in-out",
            opacity: 1,
          }
        } : {
          "svg.openSvg": {
            transition: "all .1s ease-in-out",
            opacity: 1,
          },
          "svg.closeSvg": {
            transform: "rotate(20deg) scale(0.5)",
            opacity: 0,
            visibility: "hidden",
          }
        }),
        animation: "fadeIn 0.1s ease-in-out none",
        "@keyframes fadeIn": {
          "0%": {
            opacity: 0,
            transform: "translateY(2px) scale(0.9)",
          },
          "100%": {
            opacity: 1,
            transform: "translateY(0) scale(1)",
          }
        }
      })}
    >
      {children}
    </button>
  )
}

export const WidgetButton: FunctionComponent = ({ ref }: ButtonHTMLAttributes<HTMLButtonElement>) => {
  const { open, toggleWidget, initalised, showPopupBadge, gooberCss } = useUnifiedWidget();
  const { config } = useConfig();

  if (!initalised) {
    return null;
  }

  return (
    <WidgetButtonStyled 
      ref={ref} 
      onClick={toggleWidget} 
      open={open} 
      config={config} 
      aria-label={open ? "Close widget" : showPopupBadge ? "Open widget, new message" : "Open widget"}
    >
      {showPopupBadge && !open && (
        <span
          aria-hidden="true"
          className={gooberCss({
            position: "absolute",
            top: "-4px",
            right: "-4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "20px",
            height: "20px",
            boxSizing: "border-box",
            borderRadius: "10px",
            border: "1px solid #ffffff",
            background: "#dc2626",
            color: "#ffffff",
            fontSize: "12px",
            fontWeight: 700,
            lineHeight: 1,
            animation: "badgeIn 0.25s cubic-bezier(0.20, 1.35, 0.22, 1) both",
            "@keyframes badgeIn": {
              "0%": { transform: "scale(0)" },
              "100%": { transform: "scale(1)" },
            },
          })}
        >
          !
        </span>
      )}
      <div className="icon">
        <CloseIcon className="closeSvg" />
        <OpenIcon className="openSvg" />
      </div>
      {config.buttonText && <span style={{ paddingRight: "2px" }}>{ open ? config.closeButtonText || config.buttonText : config.buttonText }</span>}
    </WidgetButtonStyled>
  )
};

